import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import StaffService from '../services/StaffService';
import { buildPasswordHash } from '../services/PasswordService';
import { buildTokenHash } from '../utils/crypto';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const staffService = useMemo(() => new StaffService(), []);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [staff, setStaff] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('Missing reset token.');
      setLoading(false);
      return;
    }
    const resolve = async () => {
      const hash = await buildTokenHash(token);
      const record = staffService
        .list()
        .find((member) => member.passwordResetTokenHash === hash);
      if (!record) {
        setStatus('This reset link is invalid or has already been used.');
        setLoading(false);
        return;
      }
      if (record.passwordResetExpiresAt && new Date(record.passwordResetExpiresAt) < new Date()) {
        setStatus('This reset link has expired.');
        setLoading(false);
        return;
      }
      setStaff(record);
      setLoading(false);
    };
    resolve();
  }, [params, staffService]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!password || password.length < 8) {
      setStatus('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setStatus('Passwords do not match.');
      return;
    }
    if (!staff) return;
    const hash = await buildPasswordHash(password);
    staffService.update({
      ...staff,
      passwordHash: hash,
      passwordMustChange: false,
      passwordResetTokenHash: null,
      passwordResetExpiresAt: null,
      lastPasswordResetAt: new Date().toISOString()
    });
    setStatus('Password updated. You can now sign in.');
    window.setTimeout(() => navigate('/'), 1500);
  };

  return (
    <div className="max-w-xl mx-auto p-6">
      <Card className="p-6 space-y-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Reset password</h2>
          <p className="sp-muted">Set a new password for your SafeguardPro account.</p>
        </div>
        {loading ? (
          <div className="text-sm sp-muted">Verifying reset link…</div>
        ) : staff ? (
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <label className="sp-label">New password</label>
              <input
                className="sp-input mt-2 w-full"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <div>
              <label className="sp-label">Confirm password</label>
              <input
                className="sp-input mt-2 w-full"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </div>
            {status && <div className="text-xs sp-warning-text">{status}</div>}
            <div className="flex items-center gap-2">
              <Button type="submit">Update password</Button>
              <Button variant="ghost" type="button" onClick={() => navigate('/')}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="text-sm sp-warning-text">{status}</div>
        )}
      </Card>
    </div>
  );
}
